import { useEffect, useRef, useState, MutableRefObject } from "react";

interface UseInViewOptions {
  /**
   * Percentage of the target element that must be visible before triggering.
   * 0 = trigger as soon as any part enters view
   * 1 = trigger only when fully visible
   * [0, 0.5, 1] = trigger at 0%, 50%, and 100% visibility
   * Default: 0
   */
  threshold?: number | number[];

  /**
   * The element that acts as the viewport for checking visibility.
   * null = use the browser viewport
   * document.querySelector('.scroll-container') = use custom scroll container
   * Default: null (browser viewport)
   */
  root?: Element | null;

  /**
   * Margin around the root element to expand/shrink the intersection area.
   * '100px' = trigger 100px before entering viewport
   * '-50px' = trigger only when 50px inside viewport
   * '10px 20px' = different margins for top/bottom and left/right
   * Default: '0px'
   */
  rootMargin?: string;

  /**
   * Whether to stop observing after the first time element becomes visible.
   * true = trigger once and stop (useful for lazy loading, animations)
   * false = trigger every time element enters/exits view
   * Default: false
   */
  triggerOnce?: boolean;
}

export function useInView<T extends Element = HTMLDivElement>(
  options: UseInViewOptions = {}
): [MutableRefObject<T | null>, boolean] {
  const {
    threshold = 0,
    root = null,
    rootMargin = "0px",
    triggerOnce = false,
  } = options;

  const ref = useRef<T | null>(null);
  const [isInView, setIsInView] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || (triggerOnce && hasTriggered)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const inView = entry.isIntersecting;
          setIsInView(inView);

          if (inView && triggerOnce) {
            setHasTriggered(true);
          }
        });
      },
      {
        threshold,
        root,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, root, rootMargin, triggerOnce, hasTriggered]);

  return [ref, isInView];
}
